import React, { useState, useMemo } from "react";
import ModalContainer from "../common/ModalContainer";
import { capitalize } from "lodash";
import CommonCollapsible from "../common/CommonCollapsible";
import { MdClose, MdKeyboardArrowDown } from "react-icons/md";
import Modal1 from "../DIsplay/Modal/Modal1";

export const FareRulesModal = ({
  setOpen: setModalOpen,
  open: modalOpen,
  fareRules,
  formattedFareRules,
}) => {
  const [mode, setMode] = useState("formatted");

  const summary = formattedFareRules?.fareruleSummary?.fareruleSummary || "";
  const otherDetails =
    formattedFareRules?.otherNecessaryDetails?.otherNecessaryDetails || "";

  const [sectionOpen, setSectionOpen] = useState({
    refund: false,
    voidRule: false,
    reissue: false,
    noShow: false,
    otherDetails: false,
  });

  const refund = formattedFareRules?.refund || {};
  const voidRule = formattedFareRules?.void || {};
  const reissue = formattedFareRules?.reissue || {};
  const noShow = formattedFareRules?.noShow || {};

  const hasFormattedContent = useMemo(() => {
    return Boolean(
      summary ||
        otherDetails ||
        Object.keys(refund).length ||
        Object.keys(voidRule).length ||
        Object.keys(reissue).length ||
        Object.keys(noShow).length
    );
  }, [summary, otherDetails, refund, voidRule, reissue, noShow]);

  const renderBadge = (label, tone = "green") => {
    const toneClasses =
      tone === "green"
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : tone === "red"
        ? "bg-red-50 text-red-700 border-red-200"
        : "bg-gray-50 text-gray-700 border-gray-200";

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${toneClasses}`}
      >
        {label}
      </span>
    );
  };

  const formatText = (text = "") => {
    const formatted = String(text)
      .split(". ")
      .map((item) => capitalize(item))
      .join(". ");
    return formatted;
  };

  const toggleSection = (key) => {
    setSectionOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <Modal1 open={modalOpen} setOpen={setModalOpen}>
      <div className="w-full max-w-[700px] py-4 flex flex-col gap-4 ">
        <div className="w-full flex items-center justify-between px-2 lg:px-4">
          <span className="">Fare rules</span>
          <button onClick={() => setModalOpen(false)}>
            <MdClose className="text-2xl" />
          </button>
        </div>
        <div className="w-full flex flex-col px-2 lg:px-4 gap-4 max-h-[70vh] overflow-y-auto scroll-hide">
          {/* Toggle: Formatted / Raw */}
          <div className="flex items-center justify-between mb-2">
            <div className="inline-flex rounded-full bg-gray-100 p-1 text-xs">
              <button
                type="button"
                onClick={() => setMode("formatted")}
                className={`px-3 py-1 rounded-full transition ${
                  mode === "formatted"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500"
                }`}
                disabled={!hasFormattedContent}
              >
                Summary
              </button>
              <button
                type="button"
                onClick={() => setMode("raw")}
                className={`px-3 py-1 rounded-full transition ${
                  mode === "raw"
                    ? "bg-white shadow-sm text-gray-900"
                    : "text-gray-500"
                }`}
                disabled={!fareRules}
              >
                Detailed fare rules
              </button>
            </div>

            {!hasFormattedContent && formattedFareRules && (
              <span className="text-[11px] text-gray-500">
                Limited structured details – raw text may have more.
              </span>
            )}
          </div>

          {/* FORMATTED VIEW */}
          {mode === "formatted" && (
            <div className="space-y-4 text-sm text-gray-800">
              {!hasFormattedContent && (
                <div className="text-xs text-gray-500">
                  No structured fare rule summary is available. Try the{" "}
                  <button
                    type="button"
                    onClick={() => setMode("raw")}
                    className="text-primary1 underline"
                  >
                    raw text
                  </button>{" "}
                  view.
                </div>
              )}

              {/* Summary (no collapsible here) */}
              {summary && (
                <section className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <h3 className="text-xs font-semibold text-gray-600 uppercase mb-1.5">
                    Overview
                  </h3>
                  <p className="text-sm leading-relaxed whitespace-pre-line">
                    {summary}
                  </p>
                </section>
              )}

              {/* Reissue / Changes */}
              {(reissue?.isReissuable != null ||
                reissue?.reissuePolicyBeforeDeparture ||
                reissue?.reissuePolicyAfterDeparture) && (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("reissue")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Changes / Reissue
                      </h3>
                    </button>

                    <div className="flex gap-2 ml-2">
                      {reissue?.isReissuable === true &&
                        renderBadge("Changeable", "green")}
                      {reissue?.isReissuable === false &&
                        renderBadge("Changes not allowed", "red")}
                      {reissue?.reissueFee &&
                        renderBadge(`Fee: ${reissue.reissueFee}`, "gray")}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.reissue ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>

                  <CommonCollapsible open={sectionOpen.reissue}>
                    <div className="w-full flex flex-col">
                      {reissue?.reissuePolicyBeforeDeparture && (
                        <div className="mb-1.5">
                          <p className="text-[11px] font-semibold text-gray-500">
                            Before departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(reissue.reissuePolicyBeforeDeparture)}
                          </p>
                        </div>
                      )}

                      {reissue?.reissuePolicyAfterDeparture && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-500">
                            After departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(reissue.reissuePolicyAfterDeparture)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CommonCollapsible>
                </section>
              )}

              {/* Refund */}
              {(refund?.isRefundable != null ||
                refund?.refundPolicyBeforeDeparture ||
                refund?.refundPolicyAfterDeparture) && (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("refund")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Refunds
                      </h3>
                    </button>

                    <div className="flex gap-2 ml-2">
                      {refund?.isRefundable === true &&
                        renderBadge("Refundable", "green")}
                      {refund?.isRefundable === false &&
                        renderBadge("Non-refundable", "red")}
                      {refund?.refundFee &&
                        refund.refundFee !== "N/A" &&
                        renderBadge(`Fee: ${refund.refundFee}`, "gray")}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.refund ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>

                  <CommonCollapsible open={sectionOpen.refund}>
                    <div className="w-full flex flex-col">
                      {refund?.refundPolicyBeforeDeparture && (
                        <div className="mb-1.5">
                          <p className="text-[11px] font-semibold text-gray-500">
                            Before departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(refund.refundPolicyBeforeDeparture)}
                          </p>
                        </div>
                      )}

                      {refund?.refundPolicyAfterDeparture && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-500">
                            After departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(refund.refundPolicyAfterDeparture)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CommonCollapsible>
                </section>
              )}

              {/* No-show */}
              {(noShow?.isNoShow != null ||
                noShow?.noShowPolicyBeforeDeparture ||
                noShow?.noShowPolicyAfterDeparture) && (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("noShow")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        No-show
                      </h3>
                    </button>

                    <div className="flex gap-2 ml-2">
                      {noShow?.isNoShow === true &&
                        renderBadge("No-show penalties apply", "red")}
                      {noShow?.noShowFee &&
                        noShow.noShowFee !== "N/A" &&
                        renderBadge(`Fee: ${noShow.noShowFee}`, "gray")}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.noShow ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>

                  <CommonCollapsible open={sectionOpen.noShow}>
                    <div className="w-full flex flex-col">
                      {noShow?.noShowPolicyBeforeDeparture && (
                        <div className="mb-1.5">
                          <p className="text-[11px] font-semibold text-gray-500">
                            Before departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(noShow.noShowPolicyBeforeDeparture)}
                          </p>
                        </div>
                      )}

                      {noShow?.noShowPolicyAfterDeparture && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-500">
                            After departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(noShow.noShowPolicyAfterDeparture)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CommonCollapsible>
                </section>
              )}

              {/* Void */}
              {(voidRule?.isVoidable != null ||
                voidRule?.voidPolicyBeforeDeparture ||
                voidRule?.voidPolicyAfterDeparture) && (
                <section className="bg-white rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("voidRule")}
                      className="flex items-center gap-1 flex-1 text-left"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Void
                      </h3>
                    </button>

                    <div className="flex gap-2 ml-2">
                      {voidRule?.isVoidable === true &&
                        renderBadge("Voidable", "green")}
                      {voidRule?.isVoidable === false &&
                        renderBadge("Not voidable", "red")}
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.voidRule ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </div>
                  </div>

                  <CommonCollapsible open={sectionOpen.voidRule}>
                    <div className="w-full flex flex-col">
                      {voidRule?.voidPolicyBeforeDeparture && (
                        <div className="mb-1.5">
                          <p className="text-[11px] font-semibold text-gray-500">
                            Before departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(voidRule.voidPolicyBeforeDeparture)}
                          </p>
                        </div>
                      )}

                      {voidRule?.voidPolicyAfterDeparture && (
                        <div>
                          <p className="text-[11px] font-semibold text-gray-500">
                            After departure
                          </p>
                          <p className="text-sm whitespace-pre-line">
                            {formatText(voidRule.voidPolicyAfterDeparture)}
                          </p>
                        </div>
                      )}
                    </div>
                  </CommonCollapsible>
                </section>
              )}

              {/* Other details */}
              {otherDetails && (
                <section className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                  <div className="flex items-center justify-between mb-1.5">
                    <button
                      type="button"
                      onClick={() => toggleSection("otherDetails")}
                      className="flex items-center gap-1 flex-1 text-left w-full justify-between"
                    >
                      <h3 className="text-xs font-semibold text-gray-600 uppercase">
                        Other conditions
                      </h3>
                      <MdKeyboardArrowDown
                        className={`text-gray-500 transition-transform duration-200 ${
                          sectionOpen.otherDetails ? "rotate-180" : ""
                        }`}
                        size={16}
                      />
                    </button>
                  </div>

                  <CommonCollapsible open={sectionOpen.otherDetails}>
                    <p className="text-sm whitespace-pre-line leading-relaxed">
                      {formatText(otherDetails)}
                    </p>
                  </CommonCollapsible>
                </section>
              )}
            </div>
          )}

          {/* RAW VIEW */}
          {mode === "raw" && (
            <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
              {fareRules ? (
                <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed font-mono">
                  {fareRules}
                </pre>
              ) : (
                <p className="text-xs text-gray-500">
                  No raw fare rule text is available for this fare.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal1>
  );
};
